(() => {
  const formatMoney = (cents, currency = 'USD') => {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
    } catch (_) {
      return `$${value.toFixed(2)}`;
    }
  };

  const bindFaqs = (root) => {
    root.querySelectorAll('[data-pm-faq-button]').forEach((button) => {
      button.addEventListener('click', () => {
        const item = button.closest('.pm-faq__item');
        const open = item?.classList.toggle('is-open');
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  };

  const bindModal = (root) => {
    const modal = root.querySelector('[data-pm-modal]');
    if (!modal) return;
    const open = () => {
      modal.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      modal.querySelector('[data-pm-modal-close]')?.focus();
    };
    const close = () => {
      modal.hidden = true;
      document.documentElement.style.overflow = '';
    };
    root.querySelectorAll('[data-pm-modal-open]').forEach((button) => button.addEventListener('click', open));
    modal.querySelectorAll('[data-pm-modal-close]').forEach((button) => button.addEventListener('click', close));
    modal.addEventListener('click', (event) => {
      if (event.target === modal) close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.hidden) close();
    });
  };

  const bindGallery = (root) => {
    const stage = root.querySelector('[data-pm-gallery-stage]');
    root.querySelectorAll('[data-pm-gallery-thumb]').forEach((button) => {
      button.addEventListener('click', () => {
        const image = button.querySelector('img');
        if (!stage || !image) return;
        stage.src = image.dataset.fullSrc || image.src;
        stage.alt = image.alt || stage.alt;
        root.querySelectorAll('[data-pm-gallery-thumb]').forEach((thumb) => thumb.classList.remove('is-active'));
        button.classList.add('is-active');
      });
    });
  };

  const bindCleanPurchase = (root) => {
    const form = root.querySelector('[data-pm-clean-form]');
    const variantInput = form?.querySelector('[name="id"]');
    const quantityInput = form?.querySelector('[name="quantity"]');
    const planInput = form?.querySelector('[name="selling_plan"]');
    const price = root.querySelector('[data-pm-price]');
    const compare = root.querySelector('[data-pm-compare]');
    const compareWrap = root.querySelector('[data-pm-compare-wrap]');
    const save = root.querySelector('[data-pm-save]');
    const discountDetail = root.querySelector('[data-pm-discount-detail]');
    const pack = root.querySelector('[data-pm-pack]');
    const offer = root.querySelector('.pm-clean-offer');
    const galleryStage = root.querySelector('[data-pm-gallery-stage]');
    const currency = root.dataset.currency || window.Shopify?.currency?.active || 'USD';
    const country = root.dataset.country || window.Shopify?.country || 'US';
    const apiVersion = root.dataset.storefrontApiVersion || '2026-07';
    const pricingCache = new Map();
    let requestSequence = 0;
    let activeTier = root.querySelector('[data-pm-tier].is-active') || root.querySelector('[data-pm-tier]');
    let activeFlavor = root.querySelector('[data-pm-flavor].is-active') || root.querySelector('[data-pm-flavor]');
    let purchaseMode = (activeFlavor?.dataset.planId || activeTier?.dataset.planId) ? 'subscription' : 'one-time';

    const getLocalPriceState = () => {
      if (!activeTier) return;
      const quantity = Number(activeTier.dataset.quantity || 1);
      const productSource = activeFlavor || activeTier;
      const unitPrice = Number(productSource.dataset.price || 0);
      const unitCompareAt = Number(productSource.dataset.compareAt || unitPrice);
      const subscriptionUnitPrice = Number(productSource.dataset.subscriptionPrice || unitPrice);
      const tierHasTotalPrice = !activeFlavor;
      const oneTimePrice = tierHasTotalPrice ? unitPrice : unitPrice * quantity;
      const subscriptionPrice = tierHasTotalPrice ? subscriptionUnitPrice : subscriptionUnitPrice * quantity;
      const activePrice = purchaseMode === 'subscription' ? subscriptionPrice : oneTimePrice;
      const baseline = tierHasTotalPrice ? Number(activeTier.dataset.compare || oneTimePrice) : unitCompareAt * quantity;
      const originalPrice = Math.max(activePrice, oneTimePrice, baseline);
      const variantId = productSource.dataset.variantId || '';
      const sellingPlanId = purchaseMode === 'subscription' ? (productSource.dataset.planId || '') : '';

      if (variantInput) variantInput.value = variantId;
      if (quantityInput) quantityInput.value = String(quantity);
      if (planInput) {
        planInput.value = sellingPlanId;
        planInput.disabled = !planInput.value;
      }

      return {
        quantity,
        variantId,
        sellingPlanId,
        activePrice,
        originalPrice,
        productPrice: oneTimePrice,
        label: activeTier.dataset.label || '1 Bag'
      };
    };

    const renderPrice = (state, livePrice = null) => {
      if (!state) return;
      const activePrice = livePrice?.finalPrice ?? state.activePrice;
      const originalPrice = Math.max(livePrice?.originalPrice ?? state.originalPrice, activePrice);
      const saved = Math.max(0, originalPrice - activePrice);
      const percent = originalPrice > 0 ? Math.round((saved / originalPrice) * 100) : 0;

      if (price) price.textContent = formatMoney(activePrice, livePrice?.currency || currency);
      if (compare) {
        compare.textContent = originalPrice > activePrice ? formatMoney(originalPrice, livePrice?.currency || currency) : '';
      }
      if (compareWrap) {
        compareWrap.hidden = originalPrice <= activePrice;
      }
      if (save) {
        save.textContent = percent > 0
          ? `Save ${percent}%`
          : 'Current price';
      }
      if (pack) pack.textContent = state.label;

      if (discountDetail) {
        const titles = livePrice?.discountTitles || [];
        const automaticSavings = livePrice?.automaticSavings || 0;
        if (titles.length && automaticSavings > 0) {
          discountDetail.textContent = `Automatic discount: ${titles.join(' + ')} · -${formatMoney(automaticSavings, livePrice.currency || currency)}`;
          discountDetail.hidden = false;
        } else if (saved > 0) {
          discountDetail.textContent = `Shopify sale price · You save ${formatMoney(saved, livePrice?.currency || currency)}`;
          discountDetail.hidden = false;
        } else {
          discountDetail.textContent = '';
          discountDetail.hidden = true;
        }
      }
    };

    const previewLivePrice = async (state) => {
      if (!state?.variantId || !window.fetch) return null;
      const cacheKey = `${state.variantId}:${state.quantity}:${state.sellingPlanId}:${country}`;
      if (pricingCache.has(cacheKey)) return pricingCache.get(cacheKey);

      const merchandiseId = state.variantId.startsWith('gid://')
        ? state.variantId
        : `gid://shopify/ProductVariant/${state.variantId}`;
      const line = { merchandiseId, quantity: state.quantity };
      if (state.sellingPlanId) {
        line.sellingPlanId = state.sellingPlanId.startsWith('gid://')
          ? state.sellingPlanId
          : `gid://shopify/SellingPlan/${state.sellingPlanId}`;
      }

      const request = fetch(`/api/${apiVersion}/graphql.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          query: `
            mutation PreviewPrice($input: CartInput) {
              cartCreate(input: $input) {
                cart {
                  cost { totalAmount { amount currencyCode } }
                  lines(first: 1) {
                    nodes {
                      quantity
                      cost {
                        compareAtAmountPerQuantity { amount currencyCode }
                        subtotalAmount { amount currencyCode }
                        totalAmount { amount currencyCode }
                      }
                      discountAllocations(lineLevelOnly: false) {
                        discountedAmount { amount currencyCode }
                        ... on CartAutomaticDiscountAllocation { title }
                        ... on CartCustomDiscountAllocation { title }
                        ... on CartCodeDiscountAllocation { code }
                      }
                    }
                  }
                }
                userErrors { message }
                warnings { message }
              }
            }
          `,
          variables: {
            input: {
              lines: [line],
              buyerIdentity: { countryCode: country }
            }
          }
        })
      }).then(async (response) => {
        if (!response.ok) throw new Error(`Live pricing failed: ${response.status}`);
        const payload = await response.json();
        const result = payload?.data?.cartCreate;
        if (!result?.cart || result.userErrors?.length) throw new Error('Live pricing returned no cart');
        const cartLine = result.cart.lines?.nodes?.[0];
        if (!cartLine) throw new Error('Live pricing returned no line');

        const toCents = (money) => Math.round(Number(money?.amount || 0) * 100);
        const lineSubtotal = toCents(cartLine.cost?.subtotalAmount);
        const finalPrice = toCents(result.cart.cost?.totalAmount || cartLine.cost?.totalAmount);
        const compareAtPerItem = toCents(cartLine.cost?.compareAtAmountPerQuantity);
        const originalPrice = Math.max(
          finalPrice,
          lineSubtotal,
          compareAtPerItem > 0 ? compareAtPerItem * state.quantity : 0
        );
        const allocations = cartLine.discountAllocations || [];
        const automaticSavings = allocations.reduce((sum, allocation) => sum + toCents(allocation.discountedAmount), 0);
        const discountTitles = allocations
          .map((allocation) => allocation.title || allocation.code)
          .filter(Boolean);

        return {
          finalPrice,
          originalPrice,
          automaticSavings,
          discountTitles,
          currency: result.cart.cost?.totalAmount?.currencyCode || currency
        };
      });

      pricingCache.set(cacheKey, request);
      request.catch(() => pricingCache.delete(cacheKey));
      return request;
    };

    const update = () => {
      const state = getLocalPriceState();
      if (!state) return;
      renderPrice(state);

      const sequence = ++requestSequence;
      if (offer) offer.setAttribute('aria-busy', 'true');
      previewLivePrice(state)
        .then((livePrice) => {
          if (sequence === requestSequence && livePrice) renderPrice(state, livePrice);
        })
        .catch(() => {
          if (sequence === requestSequence) renderPrice(state);
        })
        .finally(() => {
          if (sequence === requestSequence && offer) offer.removeAttribute('aria-busy');
        });
    };

    root.querySelectorAll('[data-pm-flavor]').forEach((button) => {
      button.addEventListener('click', () => {
        activeFlavor = button;
        root.querySelectorAll('[data-pm-flavor]').forEach((item) => item.classList.remove('is-active'));
        button.classList.add('is-active');
        if (galleryStage && button.dataset.imageUrl) {
          galleryStage.src = button.dataset.imageUrl;
          galleryStage.alt = `${button.textContent.trim()} ${galleryStage.alt}`.trim();
        }
        if (button.dataset.variantId) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', button.dataset.variantId);
          window.history.replaceState({}, '', url);
        }
        update();
      });
    });

    root.querySelectorAll('[data-pm-tier]').forEach((button) => {
      button.addEventListener('click', () => {
        activeTier = button;
        root.querySelectorAll('[data-pm-tier]').forEach((item) => item.classList.remove('is-active'));
        button.classList.add('is-active');
        update();
      });
    });

    root.querySelectorAll('[data-pm-purchase-mode]').forEach((button) => {
      button.addEventListener('click', () => {
        purchaseMode = button.dataset.pmPurchaseMode || 'one-time';
        root.querySelectorAll('[data-pm-purchase-mode]').forEach((item) => item.classList.remove('is-active'));
        button.classList.add('is-active');
        update();
      });
    });

    update();
  };

  const bindCarousels = (root) => {
    root.querySelectorAll('[data-pm-carousel]').forEach((carousel) => {
      const track = carousel.querySelector('[data-pm-carousel-track]');
      if (!track) return;
      carousel.querySelector('[data-pm-carousel-prev]')?.addEventListener('click', () => {
        track.scrollBy({ left: -Math.max(280, track.clientWidth * 0.75), behavior: 'smooth' });
      });
      carousel.querySelector('[data-pm-carousel-next]')?.addEventListener('click', () => {
        track.scrollBy({ left: Math.max(280, track.clientWidth * 0.75), behavior: 'smooth' });
      });
    });
  };

  const initialize = (root) => {
    if (root.dataset.pmReady === 'true') return;
    root.dataset.pmReady = 'true';
    bindFaqs(root);
    bindModal(root);
    bindGallery(root);
    bindCleanPurchase(root);
    bindCarousels(root);
  };

  const initializeAll = () => document.querySelectorAll('[data-pm-source]').forEach(initialize);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll);
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', initializeAll);
})();
